/**
 * Asynchronous function that returns a promise that resolves after a specified amount of time or on the next animation frame.
 * @param {number} [msToWait] - The number of milliseconds to wait before resolving the promise. If 0 or falsy, resolves on the next animation frame.
 * @returns {Promise<void>} - A promise that resolves after the specified delay or on the next animation frame.
 * @example
 * ```
 * await wait(1000); // waits for 1 second
 * ```
 */
export function wait(msToWait) {
	if (msToWait) {
		return new Promise((resolve) => setTimeout(resolve, msToWait));
	}
	return new Promise((resolve) => window.requestAnimationFrame(resolve));
}

/**
 * Capitalizes the first letter of a string.
 *
 * @param {string} string - The string to capitalize the first letter of.
 * @returns {string} - The capitalized string.
 *
 * @example
 * capitalize('hello'); // returns 'Hello'
 */
export function capitalize(string) {
	string ??= '';
	return string.charAt(0).toUpperCase() + string.slice(1);
}

export function uncapitalize(string) {
	string ??= '';
	return string.charAt(0).toLowerCase() + string.slice(1);
}

/**
 * Takes an input string with spaces and camel cases it. The first letter of the input string will be forced to lowercase.
 * @param {string} string - The string to camel case
 * @returns {string} - The camel cased string
 */
export function camelCase(string = '') {
	const [first, ...rest] = string.split(' ');
	return [first.toLowerCase(), ...rest.map(capitalize)].join('');
}

// TODO: Find library to handle all time conversions
// Currently have functionality that handles Advent times and this for BaseGameView times
export function msToMinutesSeconds(ms) {
	let minutes = Math.floor(ms / 60000);
	let seconds = ((ms % 60000) / 1000).toFixed(0);

	if (parseInt(seconds) < 10) {
		seconds = `0${seconds}`;
	} else if (parseInt(seconds) === 60) {
		minutes = 1;
		seconds = '00';
	}

	return `${minutes}:${seconds}`;
}

export function addReadyPromise(target) {
	let onReadyResolve;
	let onReadyReject;
	const isReady = new Promise((resolve, reject) => {
		onReadyResolve = resolve;
		onReadyReject = reject;
	});
	Object.assign(target, { isReady, onReadyResolve, onReadyReject });
}

export const COMPARATORS = Object.freeze({
	greaterThan(operand, targetValue) {
		return operand > targetValue;
	},

	greaterThanEqualTo(operand, targetValue) {
		return operand >= targetValue;
	},

	lessThan(operand, targetValue) {
		return operand < targetValue;
	},

	lessThanEqualTo(operand, targetValue) {
		return operand <= targetValue;
	},

	equalTo(operand, targetValue) {
		return operand === targetValue;
	},
});

export function scoreFormatter(score, scoreType) {
	if (scoreType === 'timed') {
		return msToMinutesSeconds(score);
	}

	return score.toLocaleString();
}

export function getSearchParam(paramName) {
	return new URLSearchParams(window.location.search).get(paramName);
}

/**
 * Shuffles an array using the Durstenfeld Shuffle, an optimized
 * version of Fisher-Yates - which is what lodash uses.
 *
 * @param {Array<any>} array The array to shuffle
 * @returns {Array<any>}
 */
export function shuffle(array) {
	for (let i = array.length - 1; i > 0; i -= 1) {
		const j = Math.floor(Math.random() * (i + 1));
		[array[i], array[j]] = [array[j], array[i]];
	}
	return array;
}

/**
 * Removes any properties from an object that
 * contains any of the given values to remove.
 *
 * @param {object} obj The object to prune
 * @returns Same object passed in
 */
export function pruneObject(obj, prunedValues = [undefined]) {
	Object.keys(obj).forEach((key) => {
		if (prunedValues.includes(obj[key])) {
			delete obj[key];
		}
	});

	return obj;
}

/**
 * Utility method to convert an event listener to a promise that will be resolved with the target.
 *
 * @param {object} target An object with 'once' function. Typically Backbone.Events based.
 * @param {string} eventName - The event name to listen for.
 * @returns {Promise<object>}
 */
export function promisify(target, eventName, listenerName = 'once') {
	if (!target[listenerName]) {
		return Promise.reject(new Error(`Promisify target does not have method "${listenerName}"`));
	}

	return new Promise((resolve) => target[listenerName](eventName, resolve));
}

/**
 * Takes an array and sequentially calls the callback with each item in the array. The next iteration
 * of the callback wont't execute until the previous callback resolves.
 *
 * @param {any[]} arr The array to iterate over
 * @param {function(any): Promise<any>} callback The function to call with each item in the array. It should
 * return a promise used to be used for chaining.
 * @returns {Promise<any>}
 */
export function arrayPromiseChain(arr, callback) {
	return arr.reduce(
		(prevPromise, item) => prevPromise.then(() => callback(item)),
		Promise.resolve(),
	);
}
